// What a profile block is, and what every block is handed when it draws.
//
// One catalog rather than a list in each place that needs one: the grid reads it
// to draw, the editor reads it to offer blocks that are off, and a block added
// later is one entry here plus one component in panels.tsx.

import type { AccountUser, ArtPiece, ArtSubmission } from '../../data/account.ts';

export type WidgetId =
  | 'about'
  | 'lineup'
  | 'photos'
  | 'crew'
  | 'links'
  | 'stats'
  | 'art'
  | 'countdown'
  | 'wanted'
  | 'weekend';

export interface Widget {
  id: WidgetId;
  w: number;
  style: 'panel' | 'outline' | 'plain';
  corner: 'soft' | 'sharp' | 'round' | 'circle';
  texture: 'none' | 'grain' | 'glow';
  opacity: number;
  title?: string;
}

export interface ProfilePage {
  aboutMd: string;
  links: { label: string; url: string }[];
  widgets: Widget[];
  avatarSize: number;
  accent: string | null;
  fontDisplay: string | null;
  fontBody: string | null;
  photoShape: 'rounded' | 'circle' | 'square';
}

export interface ProfilePhoto {
  id: string;
  url: string;
  caption: string;
}

// Everything a block can read or ask for. A block reaches the store and the
// session through this rather than through its own hooks, so the page fetches
// once and every block on it agrees about what it found.
export interface PanelContext {
  widget: Widget;
  editing: boolean;
  user: AccountUser | null;
  page: ProfilePage;
  photos: ProfilePhoto[];
  art: ArtPiece[];
  offers: ArtSubmission[];
  artNote: string;
  setArtNote: (note: string) => void;
  reloadArt: () => Promise<void>;
  stats: { n: string | number; label: string }[];
  activeDay: string | undefined;
  setLineupDay: (dayId: string) => void;
  setAbout: (markdown: string) => void;
  commit: () => void;
  rename: (title: string) => void;
}

export interface CatalogEntry {
  id: WidgetId;
  name: string;
  blurb: string;
  /** The width a freshly added block takes, in the same percentages the editor cycles. */
  width: number;
}

export const CATALOG: CatalogEntry[] = [
  { id: 'about', name: 'About', blurb: 'Your story, in Markdown', width: 66 },
  { id: 'lineup', name: 'Lineup', blurb: 'What you picked, day by day', width: 34 },
  { id: 'weekend', name: 'Weekend', blurb: 'Hours of music across the three days', width: 50 },
  { id: 'countdown', name: 'Countdown', blurb: 'How long until the gates open', width: 34 },
  { id: 'wanted', name: 'Most wanted', blurb: 'The artists you rated highest', width: 34 },
  { id: 'art', name: 'My art', blurb: 'Pieces you posted, and offers to the site', width: 66 },
  { id: 'photos', name: 'Photos', blurb: 'Your shelf', width: 100 },
  { id: 'crew', name: 'Crew', blurb: 'Who you are planning with', width: 34 },
  { id: 'links', name: 'Links', blurb: 'Anywhere else you are', width: 34 },
  { id: 'stats', name: 'Numbers', blurb: 'Sets, hours, ratings, crew', width: 50 },
];

export const WIDGET_NAMES: Record<WidgetId, string> = Object.fromEntries(
  CATALOG.map((entry) => [entry.id, entry.name]),
) as Record<WidgetId, string>;

export const ALL_WIDGETS: WidgetId[] = CATALOG.map((entry) => entry.id);

// The grid is twelve columns. A stored width is a percentage, because that is
// what every profile written before the grid existed holds, so it is read as one
// and rounded to the nearest column count it can fill.
export function columnSpan(width: number): number {
  const span = Math.round((Math.min(100, Math.max(25, width)) / 100) * 12);
  return Math.min(12, Math.max(3, span));
}
